import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { fileUrl } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Crown, Trophy } from "lucide-react";

const METRICS = [
  { key: "pts", label: "PTS" },
  { key: "ast", label: "AST" },
  { key: "reb", label: "RT" },
  { key: "eff", label: "EF" },
  { key: "fg_pct", label: "FG%" },
  { key: "tov", label: "TO" },
];

export default function PublicLeaders() {
  const { t } = useTranslation();
  const [metric, setMetric] = useState("pts");
  const [rows, setRows] = useState([]);
  const [tournaments, setTournaments] = useState([]);
  const [tournament, setTournament] = useState("all");
  const [category, setCategory] = useState("all");

  useEffect(() => {
    api.get("/tournaments").then((r) => setTournaments(r.data)).catch(() => {});
  }, []);

  useEffect(() => { setCategory("all"); }, [tournament]);

  useEffect(() => {
    const qs = new URLSearchParams({ metric, limit: "15" });
    if (tournament !== "all") qs.set("tournament_id", tournament);
    if (category !== "all") qs.set("category_id", category);
    api.get(`/leaders?${qs.toString()}`).then((r) => setRows(r.data));
  }, [metric, tournament, category]);

  const selectedTournament = tournaments.find((tour) => tour.id === tournament);
  const categoryOptions = selectedTournament?.categories || [];

  return (
    <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-8 py-8 md:py-12">
      <PageHeader
        title={t("leaders.title")}
        subtitle={selectedTournament ? t("leaders.subtitle_filtered", { name: selectedTournament.name }) : t("leaders.subtitle")}
        testid="page-leaders"
      />

      <div className="mb-6 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 text-ink-secondary text-xs font-heading uppercase tracking-widest">
          <Trophy size={14} className="text-orange" /> {t("common.tournament")}
        </div>
        <div className="max-w-xs flex-1 min-w-[240px]">
          <Select value={tournament} onValueChange={setTournament}>
            <SelectTrigger data-testid="leaders-tournament-filter" className="bg-navy-deep border-navy-border text-white rounded-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-navy-deep border-navy-border text-white">
              <SelectItem value="all" data-testid="leaders-tournament-option-all">{t("games.filter_tournament_all")}</SelectItem>
              {tournaments.map((tour) => (
                <SelectItem key={tour.id} value={tour.id} data-testid={`leaders-tournament-option-${tour.id}`}>
                  {tour.name}{tour.season ? ` · ${tour.season}` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {categoryOptions.length > 0 && (
          <div className="max-w-xs flex-1 min-w-[200px]">
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="leaders-category-filter" className="bg-navy-deep border-navy-border text-white rounded-sm">
                <SelectValue placeholder="Categoria" />
              </SelectTrigger>
              <SelectContent className="bg-navy-deep border-navy-border text-white">
                <SelectItem value="all">Todas as categorias</SelectItem>
                {categoryOptions.map((c) => (
                  <SelectItem key={c.id} value={c.id} data-testid={`leaders-category-option-${c.id}`}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      <Tabs value={metric} onValueChange={setMetric}>
        <TabsList className="bg-navy-deep border border-navy-border rounded-sm p-1 flex flex-wrap h-auto">
          {METRICS.map((m) => (
            <TabsTrigger key={m.key} value={m.key} data-testid={`leaders-tab-${m.key}`} className="font-heading uppercase tracking-widest text-xs data-[state=active]:bg-orange data-[state=active]:text-white rounded-sm">
              {m.label}
            </TabsTrigger>
          ))}
        </TabsList>
        {METRICS.map((m) => (
          <TabsContent key={m.key} value={m.key} className="mt-6">
            {rows.length === 0 ? (
              <div className="border border-dashed border-navy-border p-12 text-center text-ink-muted">{t("tournament.no_leaders")}</div>
            ) : (
              <div className="border border-navy-border rounded-sm overflow-hidden">
                {rows.map((r, i) => (
                  <Link
                    key={r.player.id}
                    to={`/players/${r.player.id}`}
                    className={`flex items-center gap-3 px-3 md:px-4 py-3 border-b border-navy-border hover:bg-navy-surface/40 ${i === 0 ? "bg-orange/10" : ""}`}
                  >
                    <div className="flex items-center gap-1 font-mono tabular text-xl md:text-2xl font-black w-8 md:w-10 shrink-0">
                      {i === 0 && <Crown size={14} className="text-orange" />}
                      <span className={i === 0 ? "text-orange" : "text-ink-secondary"}>{i + 1}</span>
                    </div>
                    <div className="w-9 h-9 md:w-10 md:h-10 rounded-sm bg-navy-surface border border-navy-border grid place-items-center overflow-hidden shrink-0">
                      {r.player.photo_url ? <img src={fileUrl(r.player.photo_url)} alt="" className="w-full h-full object-cover" />
                        : <span className="font-heading font-bold text-orange">{r.player.full_name?.[0]}</span>}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-heading font-bold uppercase tracking-tight text-white text-sm md:text-base truncate">{r.player.full_name}</div>
                      <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted truncate">#{r.player.jersey_number} · {r.team?.name || ""}</div>
                    </div>
                    <div className="text-center hidden sm:block">
                      <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">Per game</div>
                      <div className="font-mono tabular text-ink-secondary text-sm">{r.per_game[m.key] ?? "—"}</div>
                    </div>
                    <div className="text-center hidden sm:block">
                      <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">Games</div>
                      <div className="font-mono tabular text-ink-secondary text-sm">{r.stats.games}</div>
                    </div>
                    <div className="text-right shrink-0 min-w-[64px]">
                      <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">{m.label}</div>
                      <div className="font-mono tabular text-xl md:text-2xl font-black text-orange">
                        {r.stats[m.key] ?? 0}{m.key === "fg_pct" ? "%" : ""}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
